/**
 * SalesPreviewListView — /preview/sales  판매 현황 시안(10-28 회의용, 회의 2026-10-21 §9)
 *
 * 판매 한 건 = 한 줄(이카운트 판매조회 꼴 — 현민 2026-10-27 "한 줄로, 굵은 카드일 이유가 없다").
 * 이카운트 판매 임시 표를 읽는다(백엔드 V장 "판매 현황" 의 "10-28 회의 시안").
 *   # · 판매 번호(시안은 이카운트 일자-No.) · 거래처 · 품목(한 줄씩 최대 3줄 + 외 n건) · 상태 칩 · 판매(공급가액) · 원가(추정) · 마진(%) · 세금계산서(칸만) · 담당자
 * 품목 파트넘버를 누르면 제품 목록에서 그 번호로 찾기(새 탭), 그 밖을 누르면 판매 상세(새 탭) — 회의 §9 "상호 연결".
 * 원가를 못 찾은 줄이 있으면 마진 옆 "일부". 임대 · 자산은 칩 색으로 가른다(회의 "같은 페이지에서 색으로").
 * 줄을 누르면 상세(새 탭). 긁어 복사(드래그)면 안 연다 — lib/selection 드래그 가드.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type { SalesPreviewListQuery, SalesPreviewView, SalesPreviewViewList } from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { ROW_NO_HEAD, rowNoCell } from '../components/row-no';
import { assert } from '../lib/assert';
import { refreshKeepingFocus } from '../lib/keep-focus';
import { escapeHtml, fmtNumber } from '../lib/cells';
import { hasTextSelection } from '../lib/selection';
import {
  SALES_KIND_WORD, SALES_STATUS_TABS, itemNameOf, marginRateOf, productSearchHrefOf, salesDetailHrefOf, salesKindOf, wonText,
} from './sales-preview/words';

interface SalesPreviewListData {
  query: SalesPreviewListQuery;              // 찾기 · 진행상태 탭 — 서버가 거른다
  list: SalesPreviewViewList | null;         // 받은 묶음. null = 아직
  loading: boolean;
  error: string;
}

const TAB_HREF = '/preview/sales';
const QUERY_DEBOUNCE_MS = 300;
const COLUMN_COUNT = 10;           // 표 열 수 — 빈 줄 · 오류 줄의 colspan

export class SalesPreviewListView extends SolaView<SalesPreviewListData> {
  override events = {
    click: { '@status-tab': 'handleStatusTab', '@open': 'handleOpen' },
    input: { '@query': 'handleQuery' },
  };

  private queryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super();
    this.data = { query: { q: '', status: '' }, list: null, loading: true, error: '' };
  }

  override getTitle(): string {
    return '판매 현황 (시안) — HCLTech';
  }

  override onMount(): void {
    void this.load();
  }

  /** 본문(.pv-body)을 다시 그린다 — 찾기 칸에 치던 포커스 · 커서는 지킨다(lib/keep-focus, 2026-10-28) */
  private refreshBody(): void {
    refreshKeepingFocus(this._rootElement ?? null, '.pv-body', () => this.refreshRegion('.pv-body', () => this.renderBody()));
  }

  render(): HTMLElement {
    const place = findMenuPlace(TAB_HREF);
    assert(place !== null, `판매 현황 탭이 메뉴에 없다: ${TAB_HREF} — MENU_GROUPS 판매 › 판매 현황의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-sales-preview pv-page">
        <header class="page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
        </header>
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  // ─── 불러오기 ───
  private async load(): Promise<void> {
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshBody();
    try {
      const list = await api.fetchSalesPreviews(this.data.query);
      this.data = { ...this.data, list, loading: false };
    } catch (e) {
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody();
  }

  // ─── 그리기 ───
  private renderBody(): string {
    const rows = this.data.list === null ? [] : this.data.list.rows;
    return `
      <p class="sp-demo-note">시안 — 이카운트 판매 실데이터(2026-09-01 ~ 11-26). 원가는 같은 파트넘버 · 컨디션의 판매일 이전 최근 구매 단가로 추정. 세금계산서 · 송장은 상세에 칸만.</p>
      ${this.renderToolbar()}
      ${this.renderSummary(rows)}
      <table class="pv-table pv-sales">
        <thead><tr>${ROW_NO_HEAD}<th title="회의 §9 의 세일즈 넘버 자리 — 시안은 이카운트 일자-No.">판매 번호</th><th>거래처</th><th>품목</th><th>상태</th><th>판매(공급가)</th><th>원가(추정)</th><th>마진</th><th title="발행일 — 진행 상태와 따로(회의 §9). 이카운트에 없어 시안은 빈 칸">세금계산서</th><th>담당자</th></tr></thead>
        <tbody>${this.renderRows(rows)}</tbody>
      </table>`;
  }

  /** 도구 줄 — 진행상태 탭 · 찾기 */
  private renderToolbar(): string {
    const { query } = this.data;
    const tabs = SALES_STATUS_TABS.map((tab) => `<button type="button" class="sp-tab${tab.status === query.status ? ' active' : ''}" data-action="status-tab" data-status="${escapeHtml(tab.status)}">${tab.label}</button>`).join('');
    return `
      <div class="pc-filter-bar">
        <div class="sp-tabs">${tabs}</div>
        <label class="pc-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="search" data-action="query" placeholder="고객 · 담당자 · 파트넘버" value="${escapeHtml(query.q)}">
        </label>
      </div>`;
  }

  /** 합계 한 줄 — 건수 · 판매 · 원가(추정) · 마진 */
  private renderSummary(rows: readonly SalesPreviewView[]): string {
    const supply = rows.reduce((sum, row) => sum + (row.supply_amount === null ? 0 : row.supply_amount), 0);
    const cost = rows.reduce((sum, row) => sum + (row.cost_amount === null ? 0 : row.cost_amount), 0);
    const margin = rows.reduce((sum, row) => sum + (row.margin_amount === null ? 0 : row.margin_amount), 0);
    return `
      <div class="sp-summary">
        <span><b>${fmtNumber(rows.length)}</b>건</span>
        <span>판매 <b>${wonText(supply)}</b></span>
        <span>원가(추정) <b>${wonText(cost)}</b></span>
        <span>마진(원가 찾은 줄만) <b>${wonText(margin)}</b></span>
      </div>`;
  }

  private renderRows(rows: readonly SalesPreviewView[]): string {
    const { loading, error } = this.data;
    if (error !== '') return `<tr><td colspan="${COLUMN_COUNT}" class="text-danger">불러오지 못했습니다 — ${escapeHtml(error)}</td></tr>`;
    if (loading) return `<tr><td colspan="${COLUMN_COUNT}" class="text-muted">불러오는 중…</td></tr>`;
    if (rows.length === 0) return `<tr><td colspan="${COLUMN_COUNT}" class="text-muted">맞는 판매가 없습니다</td></tr>`;
    return rows.map((row, index) => this.renderRow(row, index)).join('');
  }

  /** 판매 한 건 = 한 줄. 줄을 누르면 상세(새 탭) */
  private renderRow(row: SalesPreviewView, index: number): string {
    const kind = salesKindOf(row.progress_status);
    return `
      <tr class="pl-row" data-action="open" data-href="${salesDetailHrefOf(row.slip_no)}" title="누르면 상세(새 탭) · 긁어 복사하면 안 열림">
        ${rowNoCell(0, index)}
        <td class="pl-sub mono sp-slip">${escapeHtml(row.slip_no)}</td>
        <td class="pl-main sp-customer-cell" title="${escapeHtml(row.customer_name ?? '')}">${row.customer_name === null ? '<span class="text-muted">—</span>' : escapeHtml(row.customer_name)}</td>
        <td class="pl-main sp-items-cell">${renderItemLines(row)}</td>
        <td><span class="sp-status sp-status-${kind}" title="이카운트: ${escapeHtml(row.progress_status ?? '')}">${SALES_KIND_WORD[kind]}</span></td>
        <td class="pl-qty">${wonText(row.supply_amount)}</td>
        <td class="pl-qty">${wonText(row.cost_amount)}</td>
        <td class="pl-qty sp-margin-cell"><span class="${marginClassOf(row)}">${wonText(row.margin_amount)}</span>${marginRateText(row)}${row.cost_missing_count > 0 ? ` <span class="sp-partial" title="원가를 못 찾은 품목 줄 ${row.cost_missing_count}개 — 원가 · 마진은 찾은 줄만">일부</span>` : ''}</td>
        <td class="pl-sub sp-invoice-cell" title="세금계산서 — 이카운트에 없어 시안은 빈 칸">—</td>
        <td class="pl-sub">${row.manager_name === null ? '<span class="text-muted">—</span>' : escapeHtml(row.manager_name)}</td>
      </tr>`;
  }

  // ─── 핸들러 ───
  handleStatusTab(_e: Event, el: HTMLElement): void {
    const status = el.dataset.status ?? '';
    this.data = { ...this.data, query: { ...this.data.query, status }, };
    void this.load();
  }

  handleQuery(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, query: { ...this.data.query, q: (el as HTMLInputElement).value } };
    if (this.queryTimer !== null) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => { this.queryTimer = null; void this.load(); }, QUERY_DEBOUNCE_MS);
  }

  handleOpen(e: Event, el: HTMLElement): void {
    if ((e.target as HTMLElement).closest('a') !== null) return;   // 품목 파트넘버 링크는 제 길로
    if (hasTextSelection()) return;
    const href = el.dataset.href;
    assert(href !== undefined, '줄은 data-href(상세 주소)를 가진다 — renderRow 가 붙였다');
    window.open(href, '_blank', 'noopener');
    e.preventDefault();
  }
}

/** 품목 칸 — 품목 한 줄씩 최대 3줄(회의 §9 "3줄, 길면 펼치기" — 펼치기는 상세가 대신). 넘치는 품목은 셋째 줄 끝 "외 n건".
 *  한 줄 = 파트넘버(제품 목록에서 찾기, 새 탭) · 품목명(넘치면 …) · ×수량 · 컨디션 */
function renderItemLines(row: SalesPreviewView): string {
  const rest = row.line_count - row.items.length;
  const lines = row.items.map((item, index) => {
    const bLast = index === row.items.length - 1;
    const name = itemNameOf(item);
    return `
      <div class="sp-item-line" title="${escapeHtml(item.spec ?? '')}">
        ${item.part_number === null ? '' : `<a class="sp-item-pn mono" href="${productSearchHrefOf(item.part_number)}" target="_blank" rel="noopener" title="제품 목록에서 이 파트넘버 찾기(새 탭)">${escapeHtml(item.part_number)}</a>`}
        <span class="sp-item-name">${escapeHtml(name)}</span>
        <span class="sp-item-qty">${item.quantity === null ? '' : `×${fmtNumber(item.quantity)}`}${item.condition_name === null ? '' : ` · ${escapeHtml(item.condition_name)}`}</span>
        ${bLast && rest > 0 ? `<span class="sp-item-rest">외 ${rest}건</span>` : ''}
      </div>`;
  });
  return lines.join('');
}

/** 마진 글 색 — 음수 붉게 */
function marginClassOf(row: SalesPreviewView): string {
  return row.margin_amount !== null && row.margin_amount < 0 ? 'sp-negative' : '';
}

/** 마진율 " (21%)" — 원가가 없으면 빈 글 */
function marginRateText(row: SalesPreviewView): string {
  const rate = marginRateOf(row);
  return rate === null ? '' : ` <span class="text-muted">(${rate.toFixed(0)}%)</span>`;
}
